import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DecorKindSchema, type DecorKind } from '@pomoshnik/shared';
import { useCatalogFilters } from '@/features/handles-catalog/useCatalogFilters';
import { Header } from '@/components/Header/Header';
import { BottomBar } from '@/components/BottomBar/BottomBar';
import { DecorFilterChips, type DecorFilterField } from './DecorFilterChips';
import { DECOR_FILTER_FIELDS } from './decorFilterFields';
import { useDecorFacets } from './useDecorFacets';

/**
 * Фильтр-экран `/decors/:kind` (PR-FLOW, ADR-0043).
 *
 * Стоит между KindPicker (2.2.1) и метод-пикером (2.2.2). Чипы выбора
 * subcategory/brand/type + кнопка «Далее». Подбор по фото/названию и «Полистай
 * каталог» ищут ВНУТРИ выбранного фильтра (Решение №1 = ДА).
 *
 * Фильтр ОПЦИОНАЛЕН: «Далее» активна и без выбора → переход на метод-пикер без
 * `?<f>` → подбор по всему kind. Лейбл именно «Далее» (не «Найти»): результатов
 * на этом экране нет — это шлюз к выбору метода.
 *
 * Поток «Далее»:
 *   1. `apply(selected)` — пишет выбранные фильтры в URL текущего экрана. Это
 *      даёт сохранение фильтров при «Назад» с метод-пикера (P-036, journey 5):
 *      история `…/:kind?<f>` → `…/:kind/method?<f>`, и «Назад» возвращает чипы.
 *   2. `navigate(.../method?<f>)` — переход на 2.2.2 с теми же фильтрами
 *      (persistent intent, R3; формат — повтор ключа, как useCatalogFilters).
 *
 * Список тут НЕ рендерится — экран чисто фильтр. Список живёт на
 * `/decors/:kind/catalog` (ветка «Полистай каталог»).
 *
 * Дочерний экран KindPicker'а → VideoButton не дублируется (P-040).
 *
 * Общий `useCatalogFilters` (decors/handles/paint) НЕ модифицируется — только
 * потребляется (apply/reset/filters).
 */
function titleForKind(kind: DecorKind): string {
  switch (kind) {
    case 'facade':
      return 'Фасады';
    case 'countertop':
      return 'Столешницы';
    case 'panel':
      return 'Корпус ЛДСП';
  }
}

export function DecorsFilter() {
  const navigate = useNavigate();
  const params = useParams<{ kind: string }>();
  const kindParse = DecorKindSchema.safeParse(params.kind);
  // Silent fallback при неизвестном kind — единый паттерн блока (DecorsCatalog,
  // PhotoPicker, MethodPicker используют тот же fallback на 'facade').
  const kind: DecorKind = kindParse.success ? kindParse.data : 'facade';

  const { filters: appliedFilters, apply, reset } = useCatalogFilters(DECOR_FILTER_FIELDS);
  const appliedSubs = useMemo(
    () => appliedFilters.subcategories ?? [],
    [appliedFilters.subcategories],
  );
  const appliedBrands = useMemo(() => appliedFilters.brands ?? [], [appliedFilters.brands]);
  const appliedTypes = useMemo(() => appliedFilters.types ?? [], [appliedFilters.types]);

  // Preview-state чипов до клика «Далее». Инициализируется applied-фильтрами
  // для поддержки deep-link и восстановления при «Назад» с метод-пикера.
  const [selectedSubs, setSelectedSubs] = useState<readonly string[]>(appliedSubs);
  const [selectedBrands, setSelectedBrands] = useState<readonly string[]>(appliedBrands);
  const [selectedTypes, setSelectedTypes] = useState<readonly string[]>(appliedTypes);

  // Опции фасета ИЗ ДАННЫХ (ADR-0054). Бренд/Тип сужены выбранной подкатегорией.
  const {
    subcategoryOptions,
    brandOptions,
    typeOptions,
    loading: facetsLoading,
  } = useDecorFacets(kind, selectedSubs, selectedBrands);

  // Pruning: после загрузки опций снимаем выбранные Бренд/Тип, которых больше нет
  // в актуальном (сужённом) наборе — иначе «невидимый» выбор уехал бы в фильтр и
  // снова дал 0. Под `!facetsLoading`, чтобы НЕ затереть deep-link выбор до
  // первой загрузки опций (R3 persistent intent).
  useEffect(() => {
    if (facetsLoading) return;
    setSelectedBrands((prev) => prev.filter((b) => brandOptions.includes(b)));
    setSelectedTypes((prev) => prev.filter((t) => typeOptions.includes(t)));
  }, [facetsLoading, brandOptions, typeOptions]);

  const onToggle = (field: DecorFilterField, value: string) => {
    const toggle = (prev: readonly string[]): readonly string[] =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value];
    if (field === 'subcategories') setSelectedSubs(toggle);
    else if (field === 'brands') setSelectedBrands(toggle);
    else setSelectedTypes(toggle);
  };

  const onResetGroup = (field: DecorFilterField) => {
    if (field === 'subcategories') setSelectedSubs([]);
    else if (field === 'brands') setSelectedBrands([]);
    else setSelectedTypes([]);
  };

  const onResetAll = () => {
    setSelectedSubs([]);
    setSelectedBrands([]);
    setSelectedTypes([]);
    reset();
  };

  const onNext = () => {
    const next = {
      subcategories: [...selectedSubs],
      brands: [...selectedBrands],
      types: [...selectedTypes],
    };
    // 1) Persist выбранные фильтры в URL фильтр-экрана (journey 5: «Назад» с
    //    метода вернёт чипы). 2) Переход на метод-пикер с теми же фильтрами.
    apply(next);
    const search = new URLSearchParams();
    for (const v of next.subcategories) search.append('subcategories', v);
    for (const v of next.brands) search.append('brands', v);
    for (const v of next.types) search.append('types', v);
    const qs = search.toString();
    navigate(`/decors/${encodeURIComponent(kind)}/method${qs ? `?${qs}` : ''}`);
  };

  const title = titleForKind(kind);
  const titleLower = title.toLowerCase();

  return (
    <main className="min-h-screen flex flex-col bg-canvas">
      <Header title={title} />

      <section className="flex-1 overflow-y-auto border-b border-icon-border p-6 flex flex-col gap-6">
        <DecorFilterChips
          subcategoryOptions={subcategoryOptions}
          brandOptions={brandOptions}
          typeOptions={typeOptions}
          optionsLoading={facetsLoading}
          selectedSubcategories={selectedSubs}
          selectedBrands={selectedBrands}
          selectedTypes={selectedTypes}
          onToggle={onToggle}
          onResetGroup={onResetGroup}
        />
        <div className="flex flex-col gap-3">
          <button
            type="button"
            onClick={onNext}
            aria-label={`Далее — выбрать метод подбора (${titleLower})`}
            className={[
              'w-full min-h-tap rounded-lg bg-cta border-b-2 border-cta-shadow text-ink',
              'font-bold text-[18px] py-3',
              'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
            ].join(' ')}
          >
            Далее
          </button>
          <button
            type="button"
            onClick={onResetAll}
            aria-label="Сбросить все фильтры"
            className={[
              'w-full min-h-tap rounded-lg bg-card border-[1.5px] border-accent text-ink',
              'text-[16px] py-3',
              'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
            ].join(' ')}
          >
            Сбросить фильтр
          </button>
        </div>
      </section>

      <BottomBar />
    </main>
  );
}
